import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_ERROR = () => 'failed';

/**
 * Loads data on mount and whenever `fetcher` changes: `{ data, isLoading, error, reload, setData }`.
 * A slower, older request never overwrites a newer one, nothing is set after unmount, and the
 * previous data stays on screen while a new one loads (`isLoading` is only true at first).
 *
 * @template T
 * @param {() => Promise<T>} fetcher Memoise it (`useCallback`) so it changes only with its inputs.
 * @param {T} empty Stable value used before the first answer and after a failure.
 * @param {(err: unknown) => string} [errorOf] Maps a failure to the `error` value (default `'failed'`).
 */
export const useAsyncLoad = (fetcher, empty, errorOf = DEFAULT_ERROR) => {
  const [state, setState] = useState({ data: empty, isLoading: true, error: null });
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(() => {
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    return fetcher()
      .then((data) => ({ data, isLoading: false, error: null }))
      .catch((err) => ({ data: empty, isLoading: false, error: errorOf(err) }))
      .then((next) => {
        if (mountedRef.current && fetchId === fetchIdRef.current) setState(next);
      });
  }, [fetcher, empty, errorOf]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const setData = useCallback((data) => setState({ data, isLoading: false, error: null }), []);

  return useMemo(() => ({ ...state, reload: load, setData }), [state, load, setData]);
};
